import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api';
import { toast } from '../utils/toast';
import { extractApiError, parseApiError } from '../utils/apiError';
import type { PaginatedProjectsResponse } from './projects';

// ─── Types ───────────────────────────────────────────────────────────────────

/** Caller's role in a workspace. Owner is implicit and cannot be assigned. */
export type WorkspaceRole = 'owner' | 'manager' | 'editor';

/** Roles that can be assigned to an invitation or an existing member. */
export type AssignableWorkspaceRole = 'manager' | 'editor';

export type InvitationStatus = 'pending' | 'accepted' | 'cancelled' | 'expired';

export interface MemberUser {
  id: number;
  username: string | null;
  full_name: string | null;
  email: string;
}

export interface WorkspaceListItem {
  uuid: string;
  name: string;
  role: WorkspaceRole;
  project_count: number;
  is_personal: boolean;
}

export interface WorkspaceMember {
  uuid: string;
  role: WorkspaceRole;
  user: MemberUser;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceInvitation {
  uuid: string;
  email: string;
  role: string;
  status: InvitationStatus;
  workspace_uuid: string;
  project_uuid: string | null;
  project_name: string | null;
  invitee: MemberUser;
  invited_by: MemberUser;
  is_automatic?: boolean;
  automatic?: boolean;
  first_seen_at: string | null;
  expires_at: string;
  created_at: string;
  updated_at: string;
}

interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ─── Workspaces (list — used to derive the caller's role in a workspace) ────

/**
 * GET /workspaces/
 * Workspaces the authenticated user belongs to, each with their role.
 */
export const useGetWorkspaces = () => {
  return useQuery<Paginated<WorkspaceListItem>>({
    queryKey: ['workspaces', 'list'],
    queryFn: async () => {
      const { data } = await apiClient.get<Paginated<WorkspaceListItem>>('/workspaces/', {
        params: { page_size: 100 },
      });
      return data;
    },
  });
};

/**
 * GET /workspaces/{workspace_uuid}/
 * Despite the path, this lists the projects in the workspace (visibility follows RBAC).
 * Tabs: all, mine, shared (guests only), archived (owner sees all; non-owners see only
 * projects they created, per RBAC).
 */
export interface WorkspaceProjectsParams {
  page?: number;
  page_size?: number;
  tab?: 'all' | 'archived' | 'mine' | 'shared';
}

export const useGetWorkspaceProjects = (
  workspaceUuid: string | undefined,
  params: WorkspaceProjectsParams = {},
  options: { enabled?: boolean } = {},
) => {
  return useQuery<PaginatedProjectsResponse>({
    queryKey: ['workspaces', workspaceUuid, 'projects', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', String(params.page));
      if (params.page_size) searchParams.set('page_size', String(params.page_size));
      if (params.tab) searchParams.set('tab', params.tab);
      const qs = searchParams.toString();
      const { data } = await apiClient.get<PaginatedProjectsResponse>(
        `/workspaces/${workspaceUuid}/${qs ? `?${qs}` : ''}`,
      );
      return data;
    },
    enabled: !!workspaceUuid && (options.enabled ?? true),
    retry: (failureCount, error: any) => {
      if (parseApiError(error).status === 403) return false;
      return failureCount < 3;
    },
  });
};

/**
 * PATCH /workspaces/{workspace_uuid}/
 * The API only exposes `name` and an opaque `settings` blob — no description or image/logo field.
 */
export const useUpdateWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ workspaceUuid, name }: { workspaceUuid: string; name: string }) => {
      const { data } = await apiClient.patch<{ data: Workspace } | Workspace>(
        `/workspaces/${workspaceUuid}/`,
        { name },
      );
      return (data as { data?: Workspace }).data ?? (data as Workspace);
    },
    onSuccess: () => {
      toast.success('Workspace updated');
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to update workspace'));
    },
  });
};

export interface Workspace {
  uuid: string;
  name: string;
  settings: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

// ─── Members ─────────────────────────────────────────────────────────────────

/**
 * GET /workspaces/{workspace_uuid}/members/
 */
export const useGetWorkspaceMembers = (workspaceUuid: string | undefined) => {
  return useQuery<Paginated<WorkspaceMember>>({
    queryKey: ['workspaces', workspaceUuid, 'members'],
    queryFn: async () => {
      const { data } = await apiClient.get<Paginated<WorkspaceMember>>(
        `/workspaces/${workspaceUuid}/members/`,
        { params: { page_size: 100 } },
      );
      return data;
    },
    enabled: !!workspaceUuid,
  });
};

/**
 * PATCH /workspaces/{workspace_uuid}/members/{member_uuid}/
 */
export const useUpdateWorkspaceMemberRole = (workspaceUuid: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      memberUuid,
      role,
    }: {
      memberUuid: string;
      role: AssignableWorkspaceRole;
    }) => {
      const { data } = await apiClient.patch(
        `/workspaces/${workspaceUuid}/members/${memberUuid}/`,
        {
          role,
        },
      );
      return data;
    },
    onSuccess: () => {
      toast.success('Member role updated');
      queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceUuid, 'members'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to update member role'));
    },
  });
};

/**
 * DELETE /workspaces/{workspace_uuid}/members/{member_uuid}/
 * Also used to let a non-owner member leave the workspace by targeting their own member record.
 */
export const useRemoveWorkspaceMember = (workspaceUuid: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (memberUuid: string) => {
      await apiClient.delete(`/workspaces/${workspaceUuid}/members/${memberUuid}/`);
      return memberUuid;
    },
    onSuccess: () => {
      toast.success('Member removed');
      queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceUuid, 'members'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to remove member'));
    },
  });
};

// ─── Invitations ─────────────────────────────────────────────────────────────

/**
 * GET /workspaces/{workspace_uuid}/invitations/?pending=true
 */
export const useGetWorkspaceInvitations = (
  workspaceUuid: string | undefined,
  params: { pending?: boolean } = {},
) => {
  return useQuery<Paginated<WorkspaceInvitation>>({
    queryKey: ['workspaces', workspaceUuid, 'invitations', params],
    queryFn: async () => {
      const { data } = await apiClient.get<Paginated<WorkspaceInvitation>>(
        `/workspaces/${workspaceUuid}/invitations/`,
        { params: { page_size: 100, ...params } },
      );
      return data;
    },
    enabled: !!workspaceUuid,
  });
};

/**
 * POST /workspaces/{workspace_uuid}/invitations/
 */
export const useInviteWorkspaceMember = (workspaceUuid: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { email: string; role: AssignableWorkspaceRole }) => {
      const { data } = await apiClient.post<{ data: WorkspaceInvitation } | WorkspaceInvitation>(
        `/workspaces/${workspaceUuid}/invitations/`,
        payload,
      );
      return (data as { data?: WorkspaceInvitation }).data ?? (data as WorkspaceInvitation);
    },
    onSuccess: (data) => {
      toast.success(`Invitation sent to ${data.email}`);
      queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceUuid, 'invitations'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceUuid, 'members'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to send invitation'));
    },
  });
};

/**
 * POST /workspaces/{workspace_uuid}/invitations/{invitation_uuid}/resend/
 */
export const useResendWorkspaceInvitation = (workspaceUuid: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (invitationUuid: string) => {
      const { data } = await apiClient.post<{ data: WorkspaceInvitation } | WorkspaceInvitation>(
        `/workspaces/${workspaceUuid}/invitations/${invitationUuid}/resend/`,
      );
      return (data as { data?: WorkspaceInvitation }).data ?? (data as WorkspaceInvitation);
    },
    onSuccess: (data) => {
      toast.success(`Invitation resent to ${data.email}`);
      queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceUuid, 'invitations'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to resend invitation'));
    },
  });
};

/**
 * DELETE /workspaces/{workspace_uuid}/invitations/{invitation_uuid}/
 */
export const useCancelWorkspaceInvitation = (workspaceUuid: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (invitationUuid: string) => {
      await apiClient.delete(`/workspaces/${workspaceUuid}/invitations/${invitationUuid}/`);
      return invitationUuid;
    },
    onSuccess: () => {
      toast.success('Invitation cancelled');
      queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceUuid, 'invitations'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceUuid, 'members'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces', 'list'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to cancel invitation'));
    },
  });
};
